import { useEffect, useState } from "react";
import { useAppDispatch } from "src/store/store";
import {
  setTurnstileToken,
  setIsTurnstileScriptLoaded,
  useTurnstileSlice,
} from "src/store/features/turnstile.slice";

interface TurnstileOptions {
  theme: "light" | "dark";
  sitekey: string;
  callback: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
}

interface TurnstileAPI {
  render: (container: string | HTMLElement, options: TurnstileOptions) => number;
  reset: (widgetId: number) => void;
}

export const TurnstileWidget = () => {
  const dispatch = useAppDispatch();
  const { turnstileToken } = useTurnstileSlice();
  const [widgetId, setWidgetId] = useState<number | null>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);

  // Load Turnstile script
  useEffect(() => {
    if (isScriptLoaded) {
      return;
    }

    const loadTurnstileScript = () => {
      // Check if script already exists
      if (document.querySelector("#turnstile-script")) {
        setIsScriptLoaded(true);
        dispatch(setIsTurnstileScriptLoaded(true));
        return;
      }

      const script = document.createElement("script");
      script.id = "turnstile-script";
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;

      script.onload = () => {
        setIsScriptLoaded(true);
        dispatch(setIsTurnstileScriptLoaded(true));
      };

      document.head.appendChild(script);
    };

    loadTurnstileScript();
  }, [isScriptLoaded, dispatch]);

  // Render widget when script is loaded
  useEffect(() => {
    if (!isScriptLoaded || !(window as any).turnstile) {
      return;
    }

    const renderWidget = () => {
      const container = document.getElementById("turnstile-container");
      if (!container) {
        console.error("Turnstile container not found");
        return;
      }

      // Clear container
      container.innerHTML = "";

      try {
        const turnstile = (window as any).turnstile as TurnstileAPI;
        const id = turnstile.render(container, {
          theme: "light",
          sitekey: "0x4AAAAAABbbTJfxRAN8C34F",
          callback: (token: string) => {
            dispatch(setTurnstileToken(token));
          },
          "expired-callback": () => {
            dispatch(setTurnstileToken(""));
          },
        });

        setWidgetId(id);
      } catch (error) {
        console.error("Failed to render Turnstile widget:", error);
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(renderWidget, 100);
    return () => clearTimeout(timer);
  }, [isScriptLoaded, dispatch]);

  // Reset widget when token is cleared to generate a fresh token
  useEffect(() => {
    if (widgetId && !turnstileToken && (window as any).turnstile) {
      try {
        const turnstile = (window as any).turnstile as TurnstileAPI;
        turnstile.reset(widgetId);
      } catch (error) {
        // dont log error for frontend view
        return;
      }
    }
  }, [turnstileToken, widgetId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (widgetId && (window as any).turnstile) {
        try {
          const turnstile = (window as any).turnstile as TurnstileAPI;
          turnstile.reset(widgetId);
        } catch (error) {
          console.error("Error during cleanup:", error);
        }
      }
    };
  }, [widgetId]);

  return (
    <div
      id="turnstile-container"
      style={{
        display: "flex",
        justifyContent: "center",
        minHeight: "65px",
      }}
    />
  );
};

export default TurnstileWidget;
